import axios from "axios";
import {
  API_BASE_URL,
  clearSession,
  getAccessToken,
  hasSession,
  refreshSession
} from "./session";

const http = axios.create({
  // baseURL: `http://localhost:3001/v2/`,
  // baseURL: `http://api.quonitrix.com/v2/`,
  // baseURL: `https://api.theassetpro.com/v2/`,
  // baseURL: `http://api.saas.janus.qa2.xencov.net/v2/`,
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json"
  }
});

http.interceptors.request.use(
  async function (config) {
    // After a page load the access token is not in memory yet: get one before the first request goes out
    if (!getAccessToken() && hasSession()) {
      try {
        await refreshSession();
      } catch (e) {
        // The request goes out without a token and the response handler below deals with the 401
      }
    }
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = token;
    }
    return config;
  },
  function (error) {
    // Do something with request error
    // console.log(error);
    return Promise.reject(error);
  }
);

// Pages that work without a login; a dead session must not bounce the user off them.
const PUBLIC_PATH = /^\/(resetPassword|public\/|error)/;
let sessionExpiredHandled = false;

// The backend answers 401 with code SESSION_EXPIRED when the token is missing, expired or revoked.
const handleSessionExpired = (hadSession) => {
  if (sessionExpiredHandled) {
    return;
  }
  const hadToken = hadSession || hasSession();
  clearSession();
  localStorage.removeItem("user");
  localStorage.removeItem("last_tag_type");

  const path = window.location.pathname;
  if (PUBLIC_PATH.test(path) || (path === "/login" && !hadToken)) {
    return;
  }
  sessionExpiredHandled = true;
  if (hadToken) {
    try {
      sessionStorage.setItem("sessionExpired", "1");
    } catch (e) {
      // Ignore storage errors
    }
  }
  // Full reload so the in-memory redux session is dropped too
  window.location.href = "/login";
};

http.interceptors.response.use(
  function (response) {
    // Any status code that lie within the range of 2xx cause this function to trigger
    // Do something with response data
    return response;
  },
  function (error) {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    // Do something with response error

    // Only redirect to error page for actual network/server connectivity issues
    // Check if there's no response (server unreachable) or network error code
    const isNetworkError = (
      !error.response && // No response means request never reached server or server is down
      (error.code === 'ERR_NETWORK' || error.code === 'ERR_CONNECTION_REFUSED') // Legacy network error message
    )

    // Log detailed error information for debugging
    console.group('🔴 API Error Details')
    console.error('Error Code:', error.code)
    console.error('Error Message:', error.message)
    console.error('Error Response:', error.response)
    console.error('Error Request:', error.request)
    console.error('Error Config:', error.config)
    console.error('Is Network Error:', isNetworkError)
    console.error('Full Error Object:', error)
    console.groupEnd()

    if (
      window.location.pathname !== "/error" &&
      isNetworkError
    ) {
      // Store error details in sessionStorage for debugging on error page
      try {
        sessionStorage.setItem('lastNetworkError', JSON.stringify({
          code: error.code,
          message: error.message,
          response: error.response,
          request: error.request,
          config: {
            url: error.config && error.config.url,
            method: error.config && error.config.method,
            baseURL: error.config && error.config.baseURL
          },
          timestamp: new Date().toISOString()
        }))
      } catch (e) {
        // Ignore storage errors
      }

      // Small delay to ensure console logs are visible before redirect
      setTimeout(() => {
        window.location.href = "/error"
      }, 100)

      return Promise.reject(error)
    }

    if (
      error.response &&
      error.response.status === 401 &&
      error.response.data &&
      error.response.data.code === "SESSION_EXPIRED"
    ) {
      const request = error.config;
      if (request && !request._retried && hasSession()) {
        // The access token ran out: get a new one and repeat the request once
        request._retried = true;
        const sentToken = request.headers && request.headers.Authorization;
        const current = getAccessToken();
        // Another request may have refreshed already while this one was in flight
        const ready = current && current !== sentToken ? Promise.resolve() : refreshSession();
        return ready.then(
          () => http(request),
          () => {
            // No new token, whatever the reason (refused, blocked, server error): the app cannot work
            // without one, so send the user to login. Only a browser that went offline keeps its session.
            if (!hasSession() || navigator.onLine !== false) {
              handleSessionExpired(true);
            }
            return Promise.reject(error);
          }
        );
      }
      handleSessionExpired();
    }

    // if(error.response.status === 401)
    //     message.error(`Invalid permission, in path ${window.location.pathname} - check your access permission!`);
    //   if(error.response.status === 500)
    //     message.error(`Unknown error, please contact provider!`);
    //   if(error.response.status === 404)
    //     message.error(`Invalid request!`);
    // }

    return Promise.reject(error);
  }
);

export default http;

export const generateQueryString = function (queryObject) {
  let queryString = "";

  if (queryObject.pagination) {
    queryString += `limit=${queryObject.pagination.pageSize
      }&offset=${queryObject.pagination.pageSize *
      (queryObject.pagination.current - 1)}`;
  }

  if (queryObject.sort && queryObject.sort.field) {
    queryString += `&sortField=${queryObject.sort.field}&sortOrder=${queryObject.sort.order === "ascend" ? 1 : -1
      }`;
  }

  let filters = [];
  for (let key in queryObject.filters) {
    if (queryObject.filters.hasOwnProperty(key) && queryObject.filters[key]) {
      filters.push(`${queryObject.filters[key]}`);
    }
  }

  if (filters) {
    queryString += `&filters=${encodeURIComponent(JSON.stringify(filters))}`;
    // queryString += `&filters=${JSON.stringify(filters)}`;
  }

  return queryString;
};

export const objectToQuery = obj =>
  Object.keys(obj)
    .map(key => `${key}=${obj[key]}`)
    .join("&");
